import { EventEmitter } from 'events';
import * as PIXI from 'pixi.js';
import EquipmentDisplay from '~/components/common/EquipmentDisplay';
import GameItemIcon from '~/components/common/GameItemIcon';
import { Colors, Config } from '~/constants';
import { ShopActions, UIActions } from '~/flux/actions';
import ShopStore from '~/flux/stores/ShopStore';
import text from '~/text';
import CanvasTools from '~/view/CanvasTools';

const SKIP_PD_PURCHASE_CONFIRM_KEY = 'dt_shop_skip_pd_purchase_confirm';

type ItemIcon = GameItemIcon & PIXI.Container;

interface ShopInventorySlot {
  rootContainer: PIXI.Container;
  icon: ItemIcon;
  priceTag: PIXI.Container;
  priceText: PIXI.Text;
}

const ShopInventoryShelves = function ({ onItemPurchaseConfirmed } = {}) {
  PIXI.Container.call(this);

  this.event_emitter = new EventEmitter();

  this.dispose = () => {
    this.event_emitter.removeAllListeners();
    for (const slot of _slots ?? []) {
      slot.icon?.dispose?.();
    }
    _slots = null;
    destroyBuyButton();
    DT_CANVAS_GLOBALS.poolSafeDestroy(this, { children: true });
  };

  const _shopInventory = ShopStore.getAll().shopInventory;
  let _slots: ShopInventorySlot[] | null = [];
  let _buyBtn;
  const myWidth = window.innerWidth * 0.42;

  const hideBuyButton = (this.hidePopups = () => {
    if (_buyBtn) {
      TweenMax.killTweensOf(_buyBtn.scale);
      TweenMax.to(_buyBtn.scale, 0.15, {
        x: 0,
        y: 0,
        onComplete: destroyBuyButton,
        onCompleteParams: [_buyBtn],
      });
    }
  });

  const destroyBuyButton = () => {
    if (!_buyBtn) {
      return;
    }
    DT_CANVAS_GLOBALS.poolSafeDestroy(_buyBtn, { children: true });
    _buyBtn = undefined;
  };

  const makeBuyButton = (event: PIXI.InteractionEvent, item_icon: ItemIcon) => {
    destroyBuyButton();

    const btn = (_buyBtn = new PIXI.Sprite());
    btn.texture = PIXI.utils.TextureCache['armory/equip_arrow.png'];
    btn.anchor.x = btn.anchor.y = 0.5;

    btn.width = 78;
    btn.scale.y = btn.scale.x;

    // flip it horizontally, so it points right
    btn.scale.x *= -1;

    const unequipText = new PIXI.Text(text('ui.buy'), {
      fontFamily: 'Courier New',
      fontWeight: 'bold',
      fontSize: '36px',
      fill: 0x00ff00,
      dropShadow: true,
      dropShadowDistance: 4,
      align: 'center',
    });
    unequipText.anchor.x = unequipText.anchor.y = 0.5;
    btn.addChild(unequipText);

    // apparently we have to flip this too
    unequipText.scale.x *= -1;

    TweenMax.from(btn.scale, 0.2, { x: 0, y: 0 });

    item_icon.addChild(btn);

    // Bring this slot (icon + price tag) above other shelf slots
    const slotRoot = item_icon.parent;
    if (slotRoot?.parent) {
      slotRoot.parent.addChild(slotRoot);
    }

    btn.interactive = btn.buttonMode = true;
    btn.tap = btn.click = () => {
      event.stopPropagation();

      const itemToBuy = item_icon.getItem();

      const completePurchase = () => {
        CanvasTools.makeFaerieSpinners(item_icon, 8);
        hideBuyButton();
        ShopActions.confirmItemPurchase(itemToBuy);
        onItemPurchaseConfirmed();
        for (const slot of _slots ?? []) {
          slot.icon?.getTooltip()?.dispose();
        }
      };

      if (itemToBuy.pd_price && localStorage.getItem(SKIP_PD_PURCHASE_CONFIRM_KEY) !== 'true') {
        const pd_amount_str = Intl.NumberFormat(Config.LOCALE, {
          maximumFractionDigits: 0,
          notation: 'standard',
        }).format(itemToBuy.pd_price);
        const item_name = EquipmentDisplay.getName(itemToBuy);
        const prompt_body = text('ui.shop.pd_purchase_confirm_body');
        UIActions.showModal({
          modal_key: 'ConfirmDecisionModal',
          modal_props: {
            title_text: text('ui.shop.pd_purchase_confirm_title'),
            prompt_text:
              typeof prompt_body === 'function'
                ? prompt_body(pd_amount_str, item_name)
                : prompt_body,
            dont_ask_again_label: text('ui.dont_ask_again'),
            dont_ask_again_storage_key: SKIP_PD_PURCHASE_CONFIRM_KEY,
            onConfirmed: completePurchase,
            onClosed: hideBuyButton,
          },
        });
        return;
      }

      completePurchase();
    };
  };

  const onItemIconClick = (item_icon: ItemIcon) => (event: PIXI.InteractionEvent) => {
    makeBuyButton(event, item_icon);
  };

  // init - create two shelves
  const shelf1 = new PIXI.Graphics();
  shelf1.beginFill(0xffffff);
  shelf1.drawRect(0, 0, myWidth, 3);
  shelf1.endFill();

  shelf1.x = -window.innerWidth * 0.008;
  shelf1.y = window.innerHeight * 0.12;
  this.addChild(shelf1);

  const shelf2 = new PIXI.Graphics();
  shelf2.beginFill(0xffffff);
  shelf2.drawRect(0, 0, myWidth, 3);
  shelf2.endFill();

  shelf2.x = -window.innerWidth * 0.008;
  shelf2.y = window.innerHeight * 0.25; // Second shelf lower
  this.addChild(shelf2);

  // init GameItemIcons
  for (let i = 0; i < _shopInventory.length; ++i) {
    if (!_shopInventory[i]) {
      continue;
    }

    const icon = new GameItemIcon(_shopInventory[i], {
      frame: false,
      showBlackwash: false,
    }) as ItemIcon;
    icon.setScale(55);

    // Determine which shelf this item belongs to (0-6 = shelf 1, 7-13 = shelf 2)
    const itemsPerShelf = 7;
    const shelfIndex = Math.floor(i / itemsPerShelf);
    const positionOnShelf = i % itemsPerShelf;

    // Position horizontally based on position within shelf
    icon.x = ((positionOnShelf + 0.82) * myWidth) / (itemsPerShelf + 1);

    // Base Y position
    const myShelf = shelfIndex === 0 ? shelf1 : shelf2;
    icon.y =
      myShelf.y +
      DT_CANVAS_GLOBALS.spacing * 1.5 -
      icon.img.height / 3 -
      55 * (1 - icon.img.scale.y) * (1 - icon.img.anchor.y);

    // do some custom nudging to make the items look like they're sitting on the shelf
    const item = icon.getItem();
    const { slot, type } = item;
    const quality_index = EquipmentDisplay.getQualityIndex(item);
    if (slot === 'helmet') {
      switch (type) {
        case 'light':
          switch (quality_index) {
            case 1:
              icon.y += 18;
              break;

            case 2:
              icon.y += 20;
              break;

            case 3:
              icon.y += 23;
              break;

            case 4:
              icon.y += 18;
              break;

            default:
              icon.y += 12;
          }
          break;

        case 'med':
          switch (quality_index) {
            case 0:
              icon.y += 12;
              break;

            case 3:
              icon.y -= 1;
              break;

            case 4:
              icon.y += 7;
              break;
          }
          break;

        case 'heavy':
          switch (quality_index) {
            case 1:
              icon.y += 8;
              break;

            case 2:
              icon.y += 4;
              break;

            default:
              icon.y += 5;
          }
          break;
      }
    } else if (slot === 'torsoe') {
      if (type === 'light' && quality_index === 0) {
        icon.y += 8;
      } else {
        icon.y += 3;
      }
    } else if (slot === 'boots') {
      switch (type) {
        case 'light':
          icon.y += 7;
          break;

        default:
          icon.y += 3;
      }
    } else if (slot === 'weapon') {
      switch (type) {
        case 'sword':
          icon.y += 28;
          break;

        case 'hammer':
          icon.y += 26;
          break;

        case 'staff':
          icon.y += 10;
          break;

        case 'dagger':
          switch (quality_index) {
            case 3:
              icon.y += 20;
              break;

            default:
              icon.y += 15;
          }
          break;
      }
    } else if (slot === 'off_hand') {
      switch (type) {
        case 'shield':
          icon.y += 3;
          break;

        case 'bow':
          icon.y += 13;
          break;

        case 'trinket':
          switch (quality_index) {
            case 3:
              icon.y += 19;
              break;

            default:
              icon.y += 16;
          }
          break;

        case 'book':
          switch (quality_index) {
            case 1:
              icon.y += 7;
              break;

            case 4:
              icon.y += 14;
              break;

            default:
              icon.y += 10;
          }
      }
    } else if (slot === 'ring') {
      icon.y += 12;
    } else {
      switch (type) {
        case 'hero_xp_boost':
          icon.y += 4;
          break;

        case 'prayer_candle':
          icon.y += 5;
          break;

        case 'equipment_dye':
        case 'miracle_dye':
          icon.y += 5;
          break;

        case 'bag':
          icon.y += 3;
          break;
      }
    }

    // Only make clickable if not sold
    if (!item.sold) {
      icon.on('pointertap', onItemIconClick(icon));
    } else {
      // Dim sold items
      icon.alpha = 0.23;
      icon.interactive = icon.buttonMode = false;
    }

    const iconYAbs = icon.y;
    const slotX = icon.x;
    const slotRoot = new PIXI.Container();
    slotRoot.x = slotX;
    slotRoot.y = myShelf.y;
    icon.x = 0;
    icon.y = iconYAbs - myShelf.y;

    const price_tag = new PIXI.Container();
    price_tag.interactive = false;
    const priceTag_bg = new PIXI.Sprite();
    if (item.sold) {
      // Grey tint for sold items
      priceTag_bg.tint = 0x888888;
    } else if (item.gold_price) {
      priceTag_bg.tint = Colors.GOLD;
    } else if (item.pd_price) {
      priceTag_bg.tint = Colors.PIXIE_DUST;
    }
    priceTag_bg.texture = PIXI.utils.TextureCache['shop/price_tag.png'];
    priceTag_bg.width = Math.min(60, window.innerWidth * 0.04);
    priceTag_bg.scale.y = priceTag_bg.scale.x;
    price_tag.addChild(priceTag_bg);

    const formattedAmount = Intl.NumberFormat(Config.LOCALE, {
      maximumFractionDigits: 0,
      notation: 'standard',
    }).format(item.gold_price || item.pd_price);

    let priceLabel: string;
    if (item.sold) {
      priceLabel = text('ui.shop.sold');
    } else if (item.gold_price) {
      priceLabel = `${formattedAmount} ${text('ui.shop.price_suffix_gold')}`;
    } else {
      priceLabel = `${formattedAmount} ${text('ui.shop.price_suffix_pixie')}`;
    }

    const priceText = new PIXI.Text(priceLabel, {
      fontFamily: 'Courier New',
      fontSize: 12,
      fontWeight: 'bold',
      fill: 0x000000,
    });

    priceText.width = Math.min(priceText.width, priceTag_bg.width * 0.9);
    priceText.scale.y = priceText.scale.x;

    priceText.x = price_tag.width / 2 - priceText.width / 2;
    priceText.y = price_tag.height / 2 - priceText.height / 2;
    price_tag.addChild(priceText);

    price_tag.x = -price_tag.width / 2;
    price_tag.y = 0;

    slotRoot.addChild(price_tag);
    slotRoot.addChild(icon);
    this.addChild(slotRoot);
    _slots.push({
      rootContainer: slotRoot,
      icon,
      priceTag: price_tag,
      priceText,
    });
  }
};
ShopInventoryShelves.prototype = Object.create(PIXI.Container.prototype);
ShopInventoryShelves.prototype.constructor = ShopInventoryShelves;
export default ShopInventoryShelves;
